// Flooring project math: area → boxes (laminate/vinyl) or planks (hardwood).
// Standard box = 20 sq ft. Board width drives plank count for hardwood.
import { toFeet, rectArea, applyWaste } from '../lib/calc'
import type { UnitSystem } from '../lib/calc'
import { ceilTo, round } from '../lib/format'
import type { AccessoryLine } from '../data/companions'
import type { Locale } from '../i18n/locale'

export interface FloorArea {
  name: string
  length: number
  width: number
}

export type FlooringType = 'hardwood' | 'laminate' | 'vinyl-plank'

// 'dimensions' = user enters one or more L × W rooms.
// 'area' = user already knows the total floor area and types it directly.
export type FlooringInputMode = 'dimensions' | 'area'

export interface FlooringInputs {
  unit: UnitSystem
  inputMode: FlooringInputMode
  areas: FloorArea[]
  totalArea: number      // sq ft (imperial) or m² (metric) — used in 'area' mode
  flooringType: FlooringType
  boardWidthIn: number   // plank width in inches, default 5
  wastePct: number       // 10-20, default 12
  transitionStrips: number  // doorways needing transition strip
  needAdhesive: boolean     // glue-down option
}

export interface FlooringResult {
  totalArea: number        // sq ft before waste
  areaWithWaste: number    // sq ft after waste
  boxes: number            // boxes of 20 sq ft
  planks: number           // individual planks (hardwood or vinyl single-count)
  underlaymentRolls: number
  spacerBags: number
  lines: AccessoryLine[]
}

export const DEFAULT_FLOORING: FlooringInputs = {
  unit: 'imperial',
  inputMode: 'dimensions',
  // Name starts empty so the "Select a room" placeholder is the dropdown's
  // default. Dimensions are neutral 12×10 ft until the user picks a room.
  areas: [{ name: '', length: 12, width: 10 }],
  totalArea: 120,
  flooringType: 'laminate',
  boardWidthIn: 5,
  wastePct: 12,
  transitionStrips: 1,
  needAdhesive: false,
}

const BOX_SQFT = 20             // standard laminate/vinyl plank box
const UNDERLAYMENT_SQFT = 200   // sq ft per roll
const SPACER_BAG = 30           // spacers per bag
const TYPICAL_PLANK_LEN_FT = 4  // average hardwood/laminate plank length for plank count

export function computeFlooring(i: FlooringInputs, locale: Locale = 'en'): FlooringResult {
  const tr = (en: string, fr: string) => (locale === 'fr' ? fr : en)
  let totalArea = 0

  if (i.inputMode === 'area') {
    // Direct area entry — m² → sq ft if metric.
    totalArea = i.unit === 'metric' ? Math.max(0, i.totalArea) * 10.7639104 : Math.max(0, i.totalArea)
  } else {
    for (const a of i.areas ?? []) {
      totalArea += rectArea(toFeet(a.length, i.unit), toFeet(a.width, i.unit))
    }
  }

  const areaWithWaste = applyWaste(totalArea, i.wastePct)

  const boxes = totalArea > 0 ? ceilTo(areaWithWaste / BOX_SQFT, 1) : 0

  // Plank count: area / (boardWidth × plankLength)
  const boardWidthFt = Math.max(1, i.boardWidthIn) / 12
  const plankAreaSqFt = boardWidthFt * TYPICAL_PLANK_LEN_FT
  const planks = totalArea > 0 ? ceilTo(areaWithWaste / plankAreaSqFt, 1) : 0

  const underlaymentRolls = totalArea > 0 ? ceilTo(areaWithWaste / UNDERLAYMENT_SQFT, 1) : 0

  // 4 spacers per linear ft of wall perimeter; approximate perimeter from sqrt of area
  const approxPerimFt = totalArea > 0 ? 4 * Math.sqrt(totalArea) : 0
  const spacerBags = totalArea > 0 ? Math.max(1, ceilTo((approxPerimFt * 4) / SPACER_BAG, 1)) : 0

  const lines: AccessoryLine[] = []

  // Underlayment: required for laminate and vinyl plank floating installs; hardwood also benefits
  const underlaymentNote = i.flooringType === 'hardwood'
    ? tr(`200 sq ft rolls · moisture + sound barrier (check manufacturer spec)`, `Rouleaux de 200 pi² · pare-humidité + insonorisation (vérifiez la fiche du fabricant)`)
    : tr(`200 sq ft rolls · combined foam/moisture barrier — check if pre-attached to flooring`, `Rouleaux de 200 pi² · mousse et pare-humidité combinés — vérifiez si déjà fixé au revêtement`)
  lines.push({
    item: `${i.flooringType === 'vinyl-plank' ? 'vinyl plank' : i.flooringType} floor underlayment roll`,
    label: tr('Underlayment', 'Sous-couche'),
    kind: 'consumable',
    qty: underlaymentRolls,
    unit: 'roll',
    note: underlaymentNote,
  })

  lines.push({
    item: 'flooring installation spacers bag',
    label: tr('Flooring spacers', 'Cales d’espacement'),
    kind: 'consumable',
    qty: spacerBags,
    unit: 'bag',
    note: tr(`Bags of ${SPACER_BAG} · maintain 3/8 in expansion gap at walls`, `Sacs de ${SPACER_BAG} · maintiennent un jeu de dilatation de 3/8 po aux murs`),
  })

  if (i.transitionStrips > 0) {
    lines.push({
      item: 'flooring transition strip threshold',
      label: tr('Transition strips', 'Baguettes de transition'),
      kind: 'consumable',
      qty: i.transitionStrips,
      unit: 'strip',
      note: tr('1 strip per doorway or floor-type change', '1 baguette par seuil de porte ou changement de revêtement'),
    })
  }

  if (i.needAdhesive) {
    const adhesiveTubs = Math.max(1, ceilTo(areaWithWaste / 40, 1))
    lines.push({
      item: 'flooring adhesive glue vinyl plank',
      label: tr('Flooring adhesive', 'Adhésif à plancher'),
      kind: 'consumable',
      qty: adhesiveTubs,
      unit: 'tub',
      note: tr('~40 sq ft per tub · for glue-down vinyl plank only · read manufacturer spec', '~40 pi² par seau · pour la pose collée du vinyle seulement · consultez la fiche du fabricant'),
    })
  } else {
    lines.push({
      item: 'flooring adhesive glue vinyl plank',
      label: tr('Flooring adhesive', 'Adhésif à plancher'),
      kind: 'consumable',
      qty: 0,
      unit: 'tub',
      note: tr('Only needed for glue-down installs — check the glue-down option above', 'Nécessaire seulement pour la pose collée — cochez l’option de pose collée ci-dessus'),
      optional: true,
    })
  }

  lines.push({ item: 'flooring pull bar tapping block installation kit', label: tr('Pull bar + tapping block', 'Barre de tirage + bloc de frappe'), kind: 'tool', note: tr('Drives boards together without damaging tongue and groove edges.', 'Assemble les planches sans abîmer les rives à rainure et languette.') })
  lines.push({ item: 'miter saw chop saw flooring', label: tr('Miter saw', 'Scie à onglets'), kind: 'tool', note: tr('For crosscuts — a circular saw works too. Rent if needed.', 'Pour les coupes transversales — une scie circulaire fait aussi l’affaire. Louez au besoin.'), optional: true })
  lines.push({ item: 'flooring jigsaw blade tool', label: tr('Jigsaw', 'Scie sauteuse'), kind: 'tool', note: tr('For cutouts around door frames, vents and angles.', 'Pour les découpes autour des cadres de porte, des grilles et des angles.'), optional: true })
  lines.push({
    item: 'flooring nailer floor stapler hardwood',
    label: tr('Floor nailer / stapler', 'Cloueuse / agrafeuse de plancher'),
    kind: 'tool',
    note: tr('Required for nail-down hardwood. Rentable at big-box stores.', 'Requise pour le bois franc cloué. Disponible en location dans les grandes surfaces.'),
    optional: i.flooringType !== 'hardwood',
  })

  return {
    totalArea: round(totalArea, 0),
    areaWithWaste: round(areaWithWaste, 0),
    boxes,
    planks,
    underlaymentRolls,
    spacerBags,
    lines,
  }
}
